import { Link } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { trackWhatsAppClick } from "@/lib/analytics";
import { openCookiePreferences } from "@/lib/consent";
import { Instagram } from "lucide-react";

export default function Footer() {
  const { t } = useLang();
  const [settings, setSettings] = useState({});
  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data || {})).catch(() => {});
  }, []);

  const whatsapp = settings.whatsapp_number || "+96891291423";
  const instagram = settings.instagram_handle || "tour_guide_oman";
  const waLink = `https://wa.me/${whatsapp.replace(/[^0-9]/g, "")}`;

  return (
    <footer className="bg-charcoal text-sand-50 mt-16 md:mt-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-14 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10">
        <div className="md:col-span-5">
          <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-3">{t.footer.made}</p>
          <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight font-light leading-[1.08]">
            Let's plan your Oman.
          </h2>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <a
              data-testid="footer-whatsapp-link"
              href={waLink}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackWhatsAppClick("footer_primary_cta")}
              className="inline-flex items-center justify-center gap-2 bg-whatsapp hover:bg-whatsapp-dark text-white rounded-full px-5 py-2.5 text-sm transition"
            >
              WhatsApp · {whatsapp}
            </a>
            <Link
              to="/contact"
              data-testid="footer-contact-link"
              className="inline-flex items-center justify-center border border-sand-50/30 hover:border-sand-50 text-sand-50 rounded-full px-5 py-2.5 text-sm transition"
            >
              {t.contact_page.form_title}
            </Link>
          </div>
        </div>
        <div className="md:col-span-2 md:col-start-8 space-y-2 text-sm">
          <p className="text-xs uppercase tracking-[0.25em] text-sand-50/50 mb-3">Site</p>
          <Link to="/tours" className="block text-sand-50/80 hover:text-terracotta">{t.nav.tours}</Link>
          <Link to="/about" className="block text-sand-50/80 hover:text-terracotta">{t.nav.about}</Link>
          <Link to="/gallery" className="block text-sand-50/80 hover:text-terracotta">{t.nav.gallery}</Link>
          <Link to="/stories" className="block text-sand-50/80 hover:text-terracotta">{t.nav.stories}</Link>
          <Link to="/contact" className="block text-sand-50/80 hover:text-terracotta">{t.nav.contact}</Link>
        </div>
        <div className="md:col-span-3 space-y-2 text-sm">
          <p className="text-xs uppercase tracking-[0.25em] text-sand-50/50 mb-3">Connect</p>
          <a href={waLink} target="_blank" rel="noreferrer" onClick={() => trackWhatsAppClick("footer_connect_link")} className="block text-sand-50/80 hover:text-terracotta">{whatsapp}</a>
          <a href={`https://instagram.com/${instagram}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sand-50/80 hover:text-terracotta">
            <Instagram size={16} /> @{instagram}
          </a>
          <p className="text-sand-50/60 pt-2">Muscat, Sultanate of Oman</p>
        </div>
      </div>
      <div className="border-t border-sand-50/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex flex-col items-center justify-center gap-2 text-xs text-sand-50/50 sm:flex-row sm:flex-wrap">
          <p>© {new Date().getFullYear()} Khalfan Oman · {t.footer.rights}</p>
          <span className="hidden sm:inline" aria-hidden="true">·</span>
          <Link to="/privacy" className="underline underline-offset-4 hover:text-sand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta" data-testid="footer-privacy-link">
            {t.footer.privacy}
          </Link>
          <span className="hidden sm:inline" aria-hidden="true">·</span>
          <button type="button" onClick={openCookiePreferences} className="underline underline-offset-4 hover:text-sand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta" data-testid="cookie-preferences-button">
            {t.footer.cookie_preferences}
          </button>
        </div>
      </div>
    </footer>
  );
}
